import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, CircularProgress, Typography, Button, Slider, Tooltip, Alert } from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon, PlayArrow as PlayIcon, Pause as PauseIcon, VolumeUp as VolumeUpIcon, VolumeOff as VolumeOffIcon, Fullscreen as FullscreenIcon, Speed as SpeedIcon } from '@mui/icons-material';
import { getFileExtension, needsExternalViewer } from '../../utils/fileTypes.ts';
import { toast } from 'react-hot-toast';
import logger from '../../utils/logger';
import { sanitizeFilename } from '../../utils/pathSanitizer';
export const DocumentViewer = ({
  open,
  onClose,
  fileName,
  fileUrl,
  fileType
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(100);

  // Video player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(100);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Text content state
  const [textContent, setTextContent] = useState('');

  // PDF blob URL state
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const videoRef = useRef(null);
  const extension = getFileExtension(fileName).toLowerCase();

  // Check if fullscreen is supported
  const isFullscreenSupported = typeof document !== 'undefined' && document.fullscreenEnabled;

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      logger.log('DocumentViewer opened:', {
        fileName,
        fileUrl,
        fileType,
        extension
      });
      setLoading(true);
      setError(null);
      setZoom(100);
      setTextContent('');
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setPdfBlobUrl(null);
    } else {
      // Clean up blob URL when closing
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
    }
  }, [open, fileUrl, fileName, fileType, extension]);

  // Load PDF files properly to handle blob URLs
  useEffect(() => {
    if (!open || extension !== 'pdf') return;
    const loadPDF = async () => {
      try {
        logger.log('[DocumentViewer] Loading PDF:', fileUrl);

        // Fetch the PDF to ensure it's accessible
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }
        const blob = await response.blob();

        // Create a new blob with explicit PDF type
        const pdfBlob = new Blob([blob], {
          type: 'application/pdf'
        });
        const blobUrl = URL.createObjectURL(pdfBlob);
        logger.log('[DocumentViewer] Created PDF blob URL:', blobUrl);
        setPdfBlobUrl(blobUrl);
      } catch (err) {
        console.error('[DocumentViewer] Error loading PDF:', err);
        setError('Failed to load PDF. The file may be corrupted or inaccessible.');
        setLoading(false);
      }
    };
    loadPDF();

    // Cleanup
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [open, fileUrl, extension]);

  // Load text content for text files
  useEffect(() => {
    if (open && ['txt', 'md', 'rtf'].includes(extension)) {
      fetch(fileUrl).then(res => res.text()).then(text => {
        setTextContent(text);
        setLoading(false);
      }).catch(err => {
        console.error('[DocumentViewer] Error loading text:', err);
        setError('Failed to load text content');
        setLoading(false);
      });
    }
  }, [open, fileUrl, extension]);

  // Video player controls
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            setIsPlaying(true);
          }).catch(error => {
            console.error('Error playing video:', error);
            setError('Unable to play video. Please try again.');
            setIsPlaying(false);
          });
        }
      }
    }
  };
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  const handleVolumeChange = (event, newValue) => {
    const vol = newValue;
    setVolume(vol);
    if (videoRef.current) {
      videoRef.current.volume = vol / 100;
    }
  };
  const handleSpeedChange = () => {
    const speeds = [0.5, 1, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackSpeed(nextSpeed);
    if (videoRef.current) {
      videoRef.current.playbackRate = nextSpeed;
    }
  };
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setDuration(videoRef.current.duration);
    }
  };
  const handleSeek = (event, newValue) => {
    const time = newValue;
    setCurrentTime(time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };
  const toggleFullscreen = () => {
    if (!videoRef.current) return;
    try {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(err => {
          console.error('Error exiting fullscreen:', err);
          toast.error('Unable to exit fullscreen mode');
        });
      } else {
        // Check if fullscreen is supported
        if (!document.fullscreenEnabled) {
          toast.info('Fullscreen is not supported in this context');
          return;
        }
        videoRef.current.requestFullscreen().catch(err => {
          console.error('Error requesting fullscreen:', err);
          toast.error('Unable to enter fullscreen mode. Try using your browser\'s fullscreen (F11)');
        });
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
      toast.error('Fullscreen is not available. Try using your browser\'s fullscreen (F11)');
    }
  };
  /**
   * Handle file download with defensive validation and filename sanitization
   */
  const handleDownload = () => {
    // Defensive validation: ensure fileUrl exists before downloading
    if (!fileUrl) {
      console.error('[DocumentViewer] Cannot download: fileUrl is missing');
      return;
    }
    
    // Defensive: sanitize filename to prevent path injection
    const safeFileName = sanitizeFilename(fileName || 'download', 'download');
    
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = safeFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };
  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Render different viewers based on file type
  const renderViewer = () => {
    // PDF Viewer
    if (extension === 'pdf') {
      // Wait for PDF blob URL to be ready
      if (!pdfBlobUrl) {
        return null; // Loading state will show
      }
      return <Box sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
          <Box sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'center',
          alignItems: 'center'
        }}>
            <Tooltip title="Zoom Out">
              <IconButton onClick={handleZoomOut} size="small" disabled={zoom <= 50}>
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{
            minWidth: 60,
            textAlign: 'center'
          }}>
              {zoom}%
            </Typography>
            <Tooltip title="Zoom In">
              <IconButton onClick={handleZoomIn} size="small" disabled={zoom >= 200}>
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{
          flex: 1,
          overflow: 'auto'
        }}>
            <iframe src={`${pdfBlobUrl}#toolbar=0&navpanes=0`} style={{
            width: `${zoom}%`,
            height: `${zoom}%`,
            border: 'none',
            transformOrigin: 'top left'
          }} onLoad={() => {
            logger.log('[DocumentViewer] PDF iframe loaded');
            setLoading(false);
          }} onError={() => {
            console.error('[DocumentViewer] PDF iframe error');
            setError('Failed to display PDF in viewer');
            setLoading(false);
          }} title={fileName} />
          </Box>
        </Box>;
    }

    // Image Viewer
    if (['jpg', 'jpeg', 'png'].includes(extension)) {
      return <Box sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
          <Box sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'center',
          alignItems: 'center'
        }}>
            <Tooltip title="Zoom Out">
              <IconButton onClick={handleZoomOut} size="small" disabled={zoom <= 50}>
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{
            minWidth: 60,
            textAlign: 'center'
          }}>
              {zoom}%
            </Typography>
            <Tooltip title="Zoom In">
              <IconButton onClick={handleZoomIn} size="small" disabled={zoom >= 200}>
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#F2F2F2'
        }}>
            <img src={fileUrl} alt={fileName} style={{
            maxWidth: `${zoom}%`,
            maxHeight: `${zoom}%`,
            objectFit: 'contain'
          }} onLoad={() => {
            logger.log('[DocumentViewer] Image loaded');
            setLoading(false);
          }} onError={() => {
            console.error('[DocumentViewer] Image load error');
            setError('Failed to load image');
            setLoading(false);
          }} />
          </Box>
        </Box>;
    }

    // Video Player
    if (extension === 'mp4') {
      return <Box sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#000'
      }}>
          {/* Video Container */}
          <Box sx={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 0 // Important for flex child
        }}>
            <video ref={videoRef} src={fileUrl} onClick={togglePlayPause} style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            cursor: 'pointer'
          }} onLoadedData={() => {
            logger.log('Video loaded');
            setLoading(false);
            if (videoRef.current) {
              setDuration(videoRef.current.duration);
              logger.log('Duration:', videoRef.current.duration);
            }
          }} onLoadedMetadata={() => {
            if (videoRef.current) {
              setDuration(videoRef.current.duration);
              logger.log('Metadata loaded, duration:', videoRef.current.duration);
            }
          }} onTimeUpdate={handleTimeUpdate} onEnded={() => setIsPlaying(false)} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onError={e => {
            console.error('Video error:', e);
            setError('Failed to load video. The file may be corrupted or in an unsupported format.');
            setLoading(false);
          }} />
          </Box>
          
          {/* Video Controls */}
          <Box sx={{
          bgcolor: 'rgba(0,0,0,0.9)',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flexShrink: 0 // Prevent controls from being compressed
        }}>
            {/* Timeline */}
            <Box sx={{
            px: 1
          }}>
              <Slider value={currentTime} max={duration || 100} onChange={handleSeek} sx={{
              color: '#2932E5',
              '& .MuiSlider-thumb': {
                width: 12,
                height: 12
              }
            }} />
              <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 0.5
            }}>
                <Typography variant="caption" sx={{
                color: '#fff'
              }}>
                  {formatTime(currentTime)}
                </Typography>
                <Typography variant="caption" sx={{
                color: '#fff'
              }}>
                  {formatTime(duration)}
                </Typography>
              </Box>
            </Box>

            {/* Control Buttons */}
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
              <IconButton 
                onClick={togglePlayPause} 
                sx={{ color: '#fff' }}
                aria-label={isPlaying ? 'Pause video' : 'Play video'}
              >
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </IconButton>

              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 150
            }}>
                <IconButton 
                  onClick={toggleMute} 
                  sx={{ color: '#fff' }} 
                  size="small"
                  aria-label={isMuted ? 'Unmute video' : 'Mute video'}
                >
                  {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                </IconButton>
                <Slider value={volume} onChange={handleVolumeChange} sx={{
                color: '#fff',
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12
                }
              }} />
              </Box>

              <Tooltip title={`Speed: ${playbackSpeed}x`}>
                <IconButton 
                  onClick={handleSpeedChange} 
                  sx={{ color: '#fff' }} 
                  size="small"
                  aria-label={`Playback speed: ${playbackSpeed}x`}
                >
                  <SpeedIcon />
                </IconButton>
              </Tooltip>

              <Box sx={{
              flex: 1
            }} />

              <Tooltip title={isFullscreenSupported ? 'Fullscreen' : 'Fullscreen not available'}>
                <span>
                  <IconButton 
                    onClick={toggleFullscreen} 
                    sx={{ color: '#fff' }} 
                    disabled={!isFullscreenSupported}
                    aria-label={isFullscreenSupported ? 'Toggle fullscreen' : 'Fullscreen not available'}
                  >
                    <FullscreenIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>;
    }

    // Text File Viewer
    if (['txt', 'md', 'rtf'].includes(extension)) {
      return <Box sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        p: 3,
        bgcolor: '#F2F2F2'
      }}>
          <pre style={{
          fontFamily: 'monospace',
          fontSize: '14px',
          color: '#4A5565',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          margin: 0
        }}>
            {textContent}
          </pre>
        </Box>;
    }

    // Office Documents - Use Microsoft Office Online Viewer
    if (needsExternalViewer(extension)) {
      // Check if fileUrl is a blob URL - Office viewer needs publicly accessible URLs
      const isBlobUrl = fileUrl.startsWith('blob:');
      if (isBlobUrl) {
        return <Box sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          p: 3
        }}>
            <Alert severity="warning" sx={{
            maxWidth: 600
          }}>
              Office documents cannot be previewed directly from uploaded files. 
              Microsoft Office Online Viewer requires publicly accessible URLs. 
              Please download the file to view it.
            </Alert>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{
            bgcolor: '#2932E5',
            '&:hover': {
              bgcolor: '#1f26b8'
            }
          }}>
              Download File
            </Button>
          </Box>;
      }
      const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
      return <Box sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
          <Alert severity="info" sx={{
          mx: 2
        }}>
            Displaying document using Microsoft Office Online Viewer. If the document doesn't load, try downloading it.
          </Alert>
          <Box sx={{
          flex: 1
        }}>
            <iframe src={officeViewerUrl} style={{
            width: '100%',
            height: '100%',
            border: 'none'
          }} onLoad={() => {
            logger.log('[DocumentViewer] Office document iframe loaded');
            setLoading(false);
          }} onError={() => {
            console.error('[DocumentViewer] Office document iframe error');
            setError('Failed to load document. Please download to view.');
            setLoading(false);
          }} title={fileName} />
          </Box>
        </Box>;
    }

    // Fallback for unsupported types
    return <Box sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2
    }}>
        <Typography variant="h6" color="text.secondary">
          Preview not available for this file type
        </Typography>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{
        bgcolor: '#2932E5',
        '&:hover': {
          bgcolor: '#1f26b8'
        }
      }}>
          Download File
        </Button>
      </Box>;
  };
  return <Dialog 
    open={open} 
    onClose={onClose} 
    maxWidth="lg" 
    fullWidth 
    aria-labelledby="document-viewer-title"
    aria-describedby="document-viewer-content"
    PaperProps={{
      sx: {
        height: '90vh',
        bgcolor: '#F2F2F2'
      }
    }}
  >
      <DialogTitle 
        id="document-viewer-title"
        sx={{
      bgcolor: '#212121',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      py: 1.5
    }}>
        <Typography variant="h6" sx={{
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
          {fileName}
        </Typography>
        
        <Box sx={{
        display: 'flex',
        gap: 1
      }}>
          <Tooltip title="Download">
            <IconButton 
              onClick={handleDownload} 
              sx={{ color: '#fff' }} 
              size="small"
              aria-label="Download file"
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton 
              onClick={onClose} 
              sx={{ color: '#fff' }} 
              size="small"
              aria-label="Close dialog"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent 
        id="document-viewer-content"
        sx={{
      p: 0,
      overflow: 'hidden',
      position: 'relative'
    }}>
        {loading && <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        zIndex: 10
      }}>
            <CircularProgress sx={{
          color: '#2932E5'
        }} />
          </Box>}

        {error && <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 3
      }}>
            <Alert severity="error" sx={{
          maxWidth: 600
        }}>
              {error}
            </Alert>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{
          bgcolor: '#2932E5',
          '&:hover': {
            bgcolor: '#1f26b8'
          }
        }}>
              Download File Instead
            </Button>
          </Box>}

        {!error && renderViewer()}
      </DialogContent>
    </Dialog>;
};
export default DocumentViewer;